/**
 * PublishToFeedDialog — confirmation modal that lets the user (or admin) share
 * a post to the B-Cafe community feed after they do something noteworthy:
 *   - Publish a new opportunity or event
 *   - Complete their profile onboarding
 *   - Create / update their venture
 *
 * The user can tweak the pre-filled copy before publishing and always has an
 * escape hatch ("Not now"). Posts are authored as the current user via the
 * public `/api/community/posts` endpoint so the identity block, likes and
 * comments all work identically to any other member post.
 *
 * When `embed` is provided the dialog previews the rich card that will appear
 * inside the post; the textarea becomes optional "additional context" copy.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Send, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useApi } from '@/hooks/use-api';
import { EmbedCard } from './EmbedCard';

const MAX_LEN = 1000;

export const PublishToFeedDialog = ({
  open,
  onOpenChange,
  title = 'Share this with the community?',
  description = 'A short note goes a long way — your fellow founders will thank you.',
  defaultText = '',
  tag = 'Update',
  media = null,      // { kind:'image'|'video', url, key, name }
  embed = null,      // { type:'opportunity'|'event'|'venture', ref_id / ref_slug, snapshot fields }
  onPublished,       // optional callback after successful post
  testid = 'publish-feed-dialog',
}) => {
  const api = useApi();
  const [body, setBody] = useState(defaultText);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setBody(defaultText); }, [open, defaultText]);

  const publish = async () => {
    const text = body.trim();
    // If we're embedding a rich card the text is optional.
    if (!text && !embed && !media) { toast.error('Add something to share'); return; }
    setBusy(true);
    try {
      const payload = {
        body: (text || '').slice(0, MAX_LEN),
        tag,
        media: media ? [media] : null,
      };
      if (embed) {
        payload.embed = {
          type: embed.type,
          ref_id: embed.ref_id || undefined,
          ref_slug: embed.ref_slug || undefined,
        };
      }
      await api.post('/api/community/posts', payload);
      toast.success('Posted to B-Cafe');
      onOpenChange(false);
      if (onPublished) onPublished();
    } catch (e) {
      toast.error('Could not publish', { description: e.message });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-plum" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_LEN))}
          className="min-h-[110px] bg-secondary/60 border-border resize-none"
          placeholder={embed ? 'Add a note (optional)…' : 'What would you like to share?'}
          data-testid={`${testid}-body`}
        />
        {embed && (
          <div className="pointer-events-none opacity-95" data-testid={`${testid}-embed-preview`}>
            <EmbedCard embed={embed} />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-2">
          <p className="text-[11px] text-muted-foreground">{body.length}/{MAX_LEN}</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testid}-skip`}>
              Not now
            </Button>
            <Button className="gap-1.5" onClick={publish} disabled={busy} data-testid={`${testid}-publish`}>
              {busy ? 'Posting…' : (<>Post to feed <Send className="h-3.5 w-3.5" /></>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PublishToFeedDialog;
